import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { databaseService } from '../services/database';
import { wakeMachines } from '../services/wake';
import { getRolePermissions } from '../services/rolePermissions';
import { createPCAgentClient } from '../services/pcAgent';
import { supabase } from '../lib/supabase';
import PCTile from '../components/PCTile';
import FakePCTile from '../components/FakePCTile';
import MarkIssueModal, { MarkIssueSubmit } from '../components/MarkIssueModal';
import Toast, { ToastData } from '../components/Toast';
import MultiplayerSessionModal from '../components/MultiplayerSessionModal';
import PlayspaceMap from '../components/PlayspaceMap';
import QueuePanel from '../components/QueuePanel';
import { QueueDequeueProvider } from '../contexts/QueueDequeueContext';
import { QueueDispatchProvider, useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import DispatchModal from '../components/DispatchModal';
import { useSettings } from '../contexts/SettingsContext';
import { DEV_TOOLS_CHANGED_EVENT, DEV_TOOLS_ENABLED, fakePcTilesActive } from '../utils/env';
import {
  Room,
  Network,
  PCInstance,
  ConfigurationRelease,
  Operator,
} from '../types/database';

interface DashboardProps {
  roomId?: string;
  isAdmin?: boolean;
  canViewBetaReleases?: boolean;
  onPCInstancesChange?: (pcInstances: PCInstance[]) => void;
  // PC ids in this room that currently have an experience running, aggregated
  // from the tiles' status polls. Lets App gate room-wide actions (Dagstart).
  onRunningExperiencesChange?: (pcIds: string[]) => void;
  refreshTrigger?: number;
}

// Left-column header of the queue dashboard ("COMPUTERS · 4 bezet · 2 vrij").
// Reads the very same per-PC reports the bars feed QueueDispatchContext from
// their own status poll, so the counts can never disagree with the bars.
// A child component because the provider is mounted by Dashboard itself.
function ComputersHeader({ total }: { total: number }) {
  const dispatch = useQueueDispatchOptional();
  const reports = dispatch ? Array.from(dispatch.pcReports.values()) : [];
  const busy = reports.filter((r) => r.online && r.running && !r.recovering).length;
  const free = reports.filter((r) => r.online && !r.running && !r.recovering).length;
  const attention = reports.filter((r) => r.recovering).length;
  const down = reports.filter((r) => !r.online).length;
  const parts = [`${busy} bezet`, `${free} vrij`];
  if (attention > 0) parts.push(`${attention} aandacht`);
  if (down > 0) parts.push(`${down} uit`);
  return (
    <div className="qdash-col-head">
      <span className="qdash-col-title">COMPUTERS</span>
      <span className="qdash-col-sub">
        {reports.length === 0 ? `${total} computer${total === 1 ? '' : 's'}` : parts.join(' · ')}
      </span>
    </div>
  );
}

// --- Identity-preserving refetch helpers -------------------------------------
// A background reload re-fetches the same rows from Supabase and gets brand-new
// objects back, even when nothing changed. Handing those straight to the tiles
// defeats React.memo and re-renders the whole room on every poll/action. These
// keep the PREVIOUS object whenever the refetched value is identical, so only
// the PCs that actually changed hand their tile a new prop.
//
// Supabase rows are plain JSON from a fixed `select('*')`, so key order is
// stable and a stringify compare is both correct and cheap at room scale
// (<= 12 PCs).
const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Whole-value swap: keep `prev` when `next` carries the same data. */
function keepIfSame<T>(prev: T, next: T): T {
  return sameJson(prev, next) ? prev : next;
}

/** Per-row merge: unchanged rows keep their old object identity, and the array
 *  itself is only replaced when at least one row (or the ordering) changed. */
function mergeRows<T extends { id: string }>(prev: T[], next: T[]): T[] {
  const byId = new Map(prev.map((row) => [row.id, row]));
  const merged = next.map((row) => {
    const old = byId.get(row.id);
    return old && sameJson(old, row) ? old : row;
  });
  const unchanged = merged.length === prev.length && merged.every((row, i) => row === prev[i]);
  return unchanged ? prev : merged;
}

/** Same idea for the per-PC installed-experience map. */
function mergeMap<K, V>(prev: Map<K, V>, next: Map<K, V>): Map<K, V> {
  let unchanged = prev.size === next.size;
  const out = new Map<K, V>();
  for (const [key, value] of next) {
    const old = prev.get(key);
    if (old !== undefined && sameJson(old, value)) {
      out.set(key, old);
    } else {
      out.set(key, value);
      unchanged = false;
    }
  }
  return unchanged ? prev : out;
}

/** Stable empty array, so a PC with no installed releases does not get a fresh
 *  `[]` (and therefore a re-render) on every parent render. */
const NO_EXPERIENCES: ConfigurationRelease[] = [];

// Calculate grid layout based on tile count: <=5 = 1 row, >5 = 2 rows
const getGridStyle = (count: number): React.CSSProperties => {
  if (count === 0) return {};
  if (count <= 5) {
    return {
      gridTemplateColumns: `repeat(${count}, 1fr)`,
      gridTemplateRows: '1fr',
    };
  }
  const cols = Math.ceil(count / 2);
  return {
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: '1fr 1fr',
  };
};

export default function Dashboard({ roomId, isAdmin = false, canViewBetaReleases = false, onPCInstancesChange, onRunningExperiencesChange, refreshTrigger = 0 }: DashboardProps) {
  // Runtime, device-local toggle (Settings page) — replaces the old compile-time
  // QUEUE_ENABLED flag. Switching it flips the dashboard and second screen
  // between queue mode and the legacy instruction-button mode live.
  const { settings } = useSettings();
  const queueEnabled = settings.queueEnabled;

  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [pcInstances, setPCInstances] = useState<PCInstance[]>([]);
  const [experiences, setExperiences] = useState<ConfigurationRelease[]>([]);
  const [pcExperiencesMap, setPcExperiencesMap] = useState<Map<string, ConfigurationRelease[]>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mpModalExperience, setMpModalExperience] = useState<ConfigurationRelease | null>(null);
  const [mpPreSelectedPcId, setMpPreSelectedPcId] = useState<string | null>(null);
  const [mpDequeueEntryIds, setMpDequeueEntryIds] = useState<string[] | null>(null);
  const [wakingAll, setWakingAll] = useState(false);
  const [gatewayPcIds, setGatewayPcIds] = useState<string[]>([]);
  const [syncingPcIds, setSyncingPcIds] = useState<Set<string>>(new Set());
  const [togglingRoomSync, setTogglingRoomSync] = useState(false);
  const [poweringGateway, setPoweringGateway] = useState(false);
  // PCs with a live experience, reported by each tile's 8s status poll.
  const [runningExperiencePcIds, setRunningExperiencePcIds] = useState<Set<string>>(new Set());
  // Legacy instruction viewer state (used when the queue setting is off).
  const [activeInstructionId, setActiveInstructionId] = useState<string | null>(null);
  const [instructionModalUrl, setInstructionModalUrl] = useState<string | null>(null);
  // Room-scoped Mark Issue (customer complaints often can't name a PC).
  const [operatorPermissions, setOperatorPermissions] = useState<string[]>([]);
  const [showRoomMarkIssue, setShowRoomMarkIssue] = useState(false);
  // Live map of the players on the room plan (headset tracking).
  const [showMap, setShowMap] = useState(false);
  const [toast, setToast] = useState<ToastData | null>(null);
  // Key (sorted failing test ids) of the upstream banner the operator
  // dismissed — a different set of failing tests re-shows the banner.
  const [dismissedUpstreamKey, setDismissedUpstreamKey] = useState<string | null>(null);
  // Staging-only (DEV_TOOLS_ENABLED): "Fake PC tiles" renders every room PC
  // WITHOUT an ip_address as a simulated tile (components/FakePCTile). On
  // staging the isolation script wipes the venue addresses; DEV-01 keeps its
  // address and stays a real tile. Re-read when Settings flips the flag.
  const [fakeTilesOn, setFakeTilesOn] = useState<boolean>(() => fakePcTilesActive());
  useEffect(() => {
    if (!DEV_TOOLS_ENABLED) return;
    const onChange = () => setFakeTilesOn(fakePcTilesActive());
    window.addEventListener(DEV_TOOLS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DEV_TOOLS_CHANGED_EVENT, onChange);
  }, []);
  const isFakePc = (pc: PCInstance): boolean => DEV_TOOLS_ENABLED && fakeTilesOn && !pc.ip_address;
  // The dispatch pop-up's hasExperience check: a fake tile has every room
  // release "installed" (there is no pc_sync_status row to consult).
  const dispatchPcExperiencesMap = useMemo(() => {
    if (!DEV_TOOLS_ENABLED || !fakeTilesOn) return pcExperiencesMap;
    const m = new Map(pcExperiencesMap);
    for (const pc of pcInstances) {
      if (!pc.ip_address) m.set(pc.id, experiences);
    }
    return m;
  }, [pcExperiencesMap, pcInstances, experiences, fakeTilesOn]);

  // Load the operator's permission strings (role permissions merged with the
  // per-operator JSONB grants) — same merge PCTile does.
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const operatorData = sessionStorage.getItem('current_operator');
        if (!operatorData) {
          return;
        }
        const operator: Operator = JSON.parse(operatorData);
        const rolePerms = await getRolePermissions(operator.role);
        const operatorPerms = operator.permissions
          ? Object.keys(operator.permissions).filter(k => operator.permissions![k] === true)
          : [];
        setOperatorPermissions(Array.from(new Set([...rolePerms, ...operatorPerms])));
      } catch (err) {
        console.error('Error loading operator permissions:', err);
      }
    };
    loadPermissions();
  }, []);

  const canMarkIssue = isAdmin || operatorPermissions.includes('mark_issue');

  // Load room data when roomId changes or refresh is triggered.
  //
  // Only the FIRST load of a room shows the loading gate. A refresh of a room
  // we already show reloads in the background: the gate unmounts the entire
  // tile grid, which throws away every tile's agent poll, websocket and local
  // UI state and makes one PC's action look like a full-page reload.
  const loadedRoomIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (roomId) {
      loadRoomData(roomId, { background: loadedRoomIdRef.current === roomId });
    } else {
      loadedRoomIdRef.current = null;
      setSelectedRoom(null);
      setIsLoading(false);
    }
  }, [roomId, refreshTrigger]);

  // Notify parent about PC instances changes
  useEffect(() => {
    if (onPCInstancesChange) {
      onPCInstancesChange(pcInstances);
    }
  }, [pcInstances, onPCInstancesChange]);

  const handleExperienceStateChange = useCallback((pcId: string, running: boolean) => {
    setRunningExperiencePcIds((prev) => {
      if (prev.has(pcId) === running) return prev;
      const next = new Set(prev);
      if (running) next.add(pcId);
      else next.delete(pcId);
      return next;
    });
  }, []);

  // Notify parent about running experiences. Keyed on the sorted id list so the
  // parent only re-renders when the membership actually changes.
  const runningExperienceKey = Array.from(runningExperiencePcIds).sort().join(',');
  const onRunningExperiencesChangeRef = React.useRef(onRunningExperiencesChange);
  useEffect(() => {
    onRunningExperiencesChangeRef.current = onRunningExperiencesChange;
  }, [onRunningExperiencesChange]);
  useEffect(() => {
    onRunningExperiencesChangeRef.current?.(runningExperienceKey ? runningExperienceKey.split(',') : []);
  }, [runningExperienceKey]);

  // On view switches this whole tree unmounts, so the tiles' own "no longer
  // busy" retractions are dropped with it. Clear the parent directly instead —
  // otherwise a stale "busy" would keep Dagstart disabled until the operator
  // navigates back. (The Dagstart handler re-checks the agents live anyway.)
  useEffect(() => {
    return () => onRunningExperiencesChangeRef.current?.([]);
  }, []);

  // `background: true` keeps the already-rendered room on screen while the rows
  // are re-fetched. Every setState below goes through the merge helpers, so a
  // reload that finds nothing changed produces no re-render at all.
  const loadRoomData = async (id: string, opts?: { background?: boolean }) => {
    if (!opts?.background) setIsLoading(true);
    setError(null);

    try {
      const room = await databaseService.getRoom(id);
      if (!room) {
        setError('Room not found');
        return;
      }
      setSelectedRoom((prev) => keepIfSame(prev, room));

      const networksList = await databaseService.getNetworksInRoom(id);
      setNetworks((prev) => mergeRows(prev, networksList));

      const pcList = await databaseService.getPCInstancesInRoom(id);
      // An open operations.pc_out_of_service row IS "this PC is out of the
      // lineup" — there is no flag on the PC row to pre-filter on, so ask for
      // all of them and merge. One extra query per room refresh, and every tile
      // keeps reading a single PC object rather than learning about two tables.
      const parked = await databaseService.getOpenOutOfService(pcList.map((pc) => pc.id));
      setPCInstances((prev) =>
        mergeRows(
          prev,
          pcList.map((pc) => ({ ...pc, out_of_service: parked.get(pc.id) ?? null })),
        ),
      );

      // Which of these PCs are out-of-band wake gateways (LSC/Tuya smart plug)?
      const gwIds = await databaseService.getGatewayPcIds(pcList.map((pc) => pc.id));
      setGatewayPcIds((prev) => keepIfSame(prev, gwIds));

      if (room.configuration_id) {
        const releasesList = await databaseService.getConfigurationReleases(
          room.configuration_id,
          'production',
          canViewBetaReleases
        );

        const pcExperiencesMapTemp = new Map<string, ConfigurationRelease[]>();
        await Promise.all(
          pcList.map(async (pc) => {
            const installedExperiences = await databaseService.getPCInstalledExperiences(
              pc.id,
              releasesList
            );
            pcExperiencesMapTemp.set(pc.id, installedExperiences);
          })
        );
        setExperiences((prev) => keepIfSame(prev, releasesList));
        setPcExperiencesMap((prev) => mergeMap(prev, pcExperiencesMapTemp));
      } else {
        setExperiences((prev) => (prev.length === 0 ? prev : []));
        setPcExperiencesMap((prev) => (prev.size === 0 ? prev : new Map()));
      }
      loadedRoomIdRef.current = id;
    } catch (err: any) {
      setError(`Failed to load room data: ${err.message}`);
    } finally {
      if (!opts?.background) setIsLoading(false);
    }
  };

  // loadRoomData is re-created each render (it closes over canViewBetaReleases),
  // so the memoised handleRefresh must reach it through a ref or it would call
  // a stale copy after an admin login flips the beta-release permission.
  const loadRoomDataRef = React.useRef(loadRoomData);
  loadRoomDataRef.current = loadRoomData;

  // The room-level release list, read by refreshPc without making it re-create
  // (and therefore re-render every tile) whenever the list is re-fetched.
  const experiencesRef = React.useRef(experiences);
  useEffect(() => { experiencesRef.current = experiences; }, [experiences]);

  // Refresh ONE PC: its infra.pc_instances row plus its installed releases.
  // This is what a tile's own actions (stop, invalidate, launch-finished) call,
  // so the other tiles never see a changed prop and — being memoised — never
  // re-render. Costs 2 targeted queries instead of the room's 4 + N.
  const refreshPc = useCallback(async (pcId: string) => {
    const [row, installed] = await Promise.all([
      databaseService.getPCInstanceById(pcId),
      databaseService.getPCInstalledExperiences(pcId, experiencesRef.current),
    ]);
    if (row) {
      setPCInstances((prev) => {
        const i = prev.findIndex((pc) => pc.id === pcId);
        if (i === -1 || sameJson(prev[i], row)) return prev;
        const next = [...prev];
        next[i] = row;
        return next;
      });
    }
    setPcExperiencesMap((prev) => {
      const old = prev.get(pcId);
      if (old && sameJson(old, installed)) return prev;
      const next = new Map(prev);
      next.set(pcId, installed);
      return next;
    });
  }, []);

  // Tiles pass their own id and get a targeted refresh; room-level actions call
  // it bare and get a full — but no longer screen-blanking — reload.
  const handleRefresh = useCallback((pcId?: string) => {
    if (pcId) {
      void refreshPc(pcId);
      return;
    }
    if (roomId) {
      void loadRoomDataRef.current(roomId, { background: true });
    }
  }, [roomId, refreshPc]);

  // Gateway PCs in this room (LSC/Tuya smart plug) that are currently offline.
  const offlineGatewayPcs = () =>
    pcInstances.filter((pc) => gatewayPcIds.includes(pc.id) && pc.status !== 'online');

  // "Zet alle computers aan": (1) wake every offline machine over both paths — a
  // magic packet from this device AND the DB flag for online agent peers — and
  // (2) power-cycle any offline gateway PC out-of-band so it boots even when the
  // whole network is dark, after which it wakes the rest of its network.
  //
  // The DB flag alone is not enough: it needs an agent already up on that network
  // to act on it, which is never true for the room this button exists for.
  const handleWakeAllInRoom = async () => {
    if (!roomId) return;
    setWakingAll(true);
    try {
      const targets = (await databaseService.getWakeTargetsInRoom(roomId)).filter(
        (pc) => pc.status !== 'online'
      );
      await wakeMachines(targets);
      for (const gw of offlineGatewayPcs()) {
        const res = await databaseService.powerOnGateway(gw.id);
        if (!res.success) {
          console.warn(`Gateway power-on failed for ${gw.pc_name}: ${res.error}`);
        }
      }
      handleRefresh();
    } finally {
      setWakingAll(false);
    }
  };

  // Dedicated, explicit gateway power-on (hard plug power-cycle). Offline-only +
  // confirm, so a running gateway is never hard-cycled by accident.
  const handlePowerOnGateways = async () => {
    const gateways = offlineGatewayPcs();
    if (gateways.length === 0) return;
    const names = gateways.map((g) => g.pc_name || 'gateway').join(', ');
    if (!window.confirm(`Gateway-pc hard in-/uitschakelen om op te starten: ${names}?`)) return;
    setPoweringGateway(true);
    try {
      for (const gw of gateways) {
        const res = await databaseService.powerOnGateway(gw.id);
        if (!res.success) {
          alert(`Gateway ${gw.pc_name} aanzetten mislukt: ${res.error || 'onbekende fout'}`);
        }
      }
      handleRefresh();
    } finally {
      setPoweringGateway(false);
    }
  };

  const buildInstructionImageUrl = (imagePath: string): string => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn('[INSTRUCTION] VITE_SUPABASE_URL is not set; instruction image URL will be invalid');
    }
    return `${supabaseUrl}/storage/v1/object/public/${imagePath}`;
  };

  const handleInstructionToggle = async (exp: ConfigurationRelease) => {
    const expId = exp.experience_id;
    const hasElectron = !!window.electron;
    const isToggleHide = activeInstructionId === expId;

    console.log('[INSTRUCTION] toggle clicked', {
      expId,
      branch: isToggleHide ? 'hide' : 'show',
      activeInstructionId,
      hasElectron,
      imagePath: exp.experiences?.instruction_image_path ?? null,
    });

    if (isToggleHide) {
      setActiveInstructionId(null);
      if (window.electron) {
        try {
          await window.electron.instruction.hide();
          console.log('[INSTRUCTION] hide() resolved');
        } catch (err) {
          console.error('[INSTRUCTION] hide() rejected', err);
        }
      } else {
        setInstructionModalUrl(null);
      }
    } else {
      const imagePath = exp.experiences?.instruction_image_path;
      if (!imagePath) {
        console.warn('[INSTRUCTION] show aborted — no instruction_image_path on experience', expId);
        return;
      }

      const imageUrl = buildInstructionImageUrl(imagePath);
      console.log('[INSTRUCTION] show() built imageUrl', imageUrl);
      setActiveInstructionId(expId);

      if (window.electron) {
        try {
          await window.electron.instruction.show(imageUrl);
          console.log('[INSTRUCTION] show() resolved');
        } catch (err) {
          console.error('[INSTRUCTION] show() rejected', err);
        }
      } else {
        setInstructionModalUrl(imageUrl);
      }
    }
  };

  // Manage the second-screen queue-display window lifecycle (queue mode only).
  // Show on Dashboard mount, hide on unmount, and re-push the locationId/roomId
  // context whenever the operator switches rooms so the display rebinds to the
  // new venue queue.
  // A venue with ONE touchscreen has no second screen to put the queue on, so
  // the display window would land on the operator's own screen: fullscreen,
  // frameless, always on top, with no way back except alt+F4. Auto-open only
  // where there is somewhere to open it; otherwise the operator opens it from
  // the button and taps it to get out again.
  const [displayPlacement, setDisplayPlacement] = useState<{ hasSecondary: boolean; visible: boolean } | null>(null);
  const [displayOpen, setDisplayOpen] = useState(false);

  useEffect(() => {
    if (!queueEnabled) {
      setDisplayPlacement(null);
      return;
    }
    let cancelled = false;
    void window.electron?.queueDisplay?.placement?.().then((p) => {
      if (cancelled || !p) return;
      setDisplayPlacement({ hasSecondary: p.hasSecondary, visible: p.visible });
      if (p.hasSecondary) {
        window.electron?.queueDisplay?.show?.();
        setDisplayOpen(true);
      }
    }).catch(() => {
      /* not running in Electron (web/tablet build): no second screen to manage */
    });
    return () => {
      cancelled = true;
      window.electron?.queueDisplay?.hide?.();
      setDisplayOpen(false);
    };
  }, [queueEnabled]);

  const toggleQueueDisplay = useCallback(() => {
    if (displayOpen) {
      void window.electron?.queueDisplay?.close?.();
      setDisplayOpen(false);
    } else {
      void window.electron?.queueDisplay?.show?.();
      setDisplayOpen(true);
    }
  }, [displayOpen]);

  useEffect(() => {
    if (!queueEnabled) return;
    if (!selectedRoom) return;
    const locationId = selectedRoom.location ?? null;
    window.electron?.queueDisplay?.setContext?.({
      locationId,
      roomId: selectedRoom.id,
    });
  }, [selectedRoom, queueEnabled]);

  // Hide the instruction window/state whenever the room changes, and whenever we
  // switch into queue mode (where there should never be an instruction window
  // open). Hiding an already-hidden window is a harmless no-op in queue mode.
  useEffect(() => {
    setActiveInstructionId(null);
    if (window.electron?.instruction) {
      window.electron.instruction.hide();
    } else {
      setInstructionModalUrl(null);
    }
  }, [roomId, queueEnabled]);

  // If the secondary instruction window is closed externally (manual close,
  // monitor unplugged, crash), main fires `instruction:closed` so we can clear
  // local state — otherwise the toggle button gets stuck in the "active" state
  // and the next click is a no-op hide.
  useEffect(() => {
    if (!window.electron?.instruction?.onClosed) return;
    window.electron.instruction.onClosed(() => {
      console.log('[INSTRUCTION] viewer window closed externally — clearing activeInstructionId');
      setActiveInstructionId(null);
    });
  }, []);

  // Mirror main-process logs into the renderer DevTools console.
  useEffect(() => {
    if (!window.electron?.onMainLog) return;
    window.electron.onMainLog(({ level, tag, args }) => {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      fn(`[main:${tag}]`, ...args);
    });
  }, []);

  // `dequeueEntryIds` comes from the queue dispatch pop-up: the session modal
  // pops those visitors once the session is running.
  // useCallback, not a plain function: it is handed to every tile as a prop, so
  // a new identity each render would re-render the whole grid past React.memo.
  const handleMultiplayerSelect = useCallback((
    release: ConfigurationRelease,
    pcId: string,
    dequeueEntryIds?: string[],
  ) => {
    setMpModalExperience(release);
    setMpPreSelectedPcId(pcId);
    setMpDequeueEntryIds(dequeueEntryIds ?? null);
  }, []);

  // Room-scoped Mark Issue: ALWAYS a direct operations.incidents insert
  // (pc_instance_id null — there is no agent to route through). Never throws
  // (MarkIssueModal relies on that).
  const handleRoomMarkIssueSubmit = async ({ category, severity, note }: MarkIssueSubmit) => {
    let operatorId: string | undefined;
    try {
      const operatorData = sessionStorage.getItem('current_operator');
      operatorId = operatorData ? JSON.parse(operatorData).id : undefined;
    } catch {
      operatorId = undefined;
    }
    const message = note ? `[${category.label}] ${note}` : `[${category.label}]`;

    const ok = await databaseService.insertIncident({
      severity,
      source: 'operator',
      message,
      pc_instance_id: null,
      room_id: roomId ?? null,
      metadata: {
        category: category.key,
        note,
        operator_id: operatorId ?? null,
        room_scoped: true,
      },
    });

    setShowRoomMarkIssue(false);
    setToast(
      ok
        ? { kind: 'success', message: `Issue marked for ${selectedRoom?.name || 'room'}` }
        : { kind: 'error', message: 'Failed to mark room issue' }
    );
  };

  // Group PCs by network
  // All room PCs in ONE grid ordered by player_index — a room can span
  // multiple networks (e.g. Heuvel1's Cleopatra + Andromeda after the swarm
  // upstream split), and the network boundary is an infra detail operators
  // shouldn't see on the dashboard.
  const sortedPcs = useMemo(
    () =>
      [...pcInstances].sort((a, b) => {
        const ai = a.player_index ?? Number.MAX_SAFE_INTEGER;
        const bi = b.player_index ?? Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a.pc_name || '').localeCompare(b.pc_name || '');
      }),
    [pcInstances],
  );

  // The lineup, and everything parked out of it. Splitting here rather than
  // inside the bar list is what gets a broken PC out of the operator's field of
  // view entirely: the lineup stays a list of machines that can take a visitor,
  // so counting free stations is a glance again (Dirk 2026-09-16).
  const lineupPcs = useMemo(() => sortedPcs.filter((pc) => !pc.out_of_service), [sortedPcs]);
  const parkedPcs = useMemo(() => sortedPcs.filter((pc) => !!pc.out_of_service), [sortedPcs]);

  // Live build-sync activity for the room banner: initial load + Supabase
  // Realtime on infra.pc_sync_status (DeploymentView pattern), filtered
  // client-side to this room's PCs.
  const roomPcIdsKey = pcInstances.map((pc) => pc.id).sort().join(',');
  useEffect(() => {
    const pcIds = roomPcIdsKey ? roomPcIdsKey.split(',') : [];
    if (pcIds.length === 0) {
      setSyncingPcIds(new Set());
      return;
    }
    let cancelled = false;
    const idSet = new Set(pcIds);
    // sync row id -> {pc id, last progress write}. Agents write progress every
    // ~10s while downloading, so a row without a fresh updated_at is a dead
    // sync (agent restarted mid-download, historic leftovers) — never count it.
    const activeRows = new Map<string, { pcId: string; updatedAt: number }>();
    const STALE_MS = 3 * 60 * 1000;

    const rowTime = (row: any): number => {
      const t = row?.updated_at ? new Date(row.updated_at).getTime() : NaN;
      return Number.isFinite(t) ? t : Date.now();
    };

    const recompute = () => {
      const now = Date.now();
      const ids = new Set<string>();
      for (const [rowId, info] of activeRows) {
        if (now - info.updatedAt > STALE_MS) {
          activeRows.delete(rowId);
          continue;
        }
        ids.add(info.pcId);
      }
      setSyncingPcIds(ids);
    };

    const refetch = () => {
      databaseService.getSyncStatusByPCIds(pcIds).then((rows) => {
        if (cancelled) return;
        activeRows.clear();
        for (const row of rows) {
          if (row.sync_status === 'downloading') {
            activeRows.set(row.id, { pcId: row.pc_instance_id, updatedAt: rowTime(row) });
          }
        }
        recompute();
      });
    };
    refetch();

    const channel = supabase
      .channel(`dashboard-sync-${roomId || 'room'}`)
      .on('postgres_changes', { event: '*', schema: 'infra', table: 'pc_sync_status' }, (payload: any) => {
        const row = (payload.new || payload.old) as any;
        if (!row || !idSet.has(row.pc_instance_id)) return;
        if (payload.eventType !== 'DELETE' && payload.new?.sync_status === 'downloading') {
          activeRows.set(payload.new.id, { pcId: payload.new.pc_instance_id, updatedAt: rowTime(payload.new) });
        } else if (row.id) {
          activeRows.delete(row.id);
        }
        recompute();
      })
      .subscribe();

    // Self-heal: Realtime delivery is lossy across reconnects/tab sleeps, and
    // a missed downloading→installed event would otherwise stick forever.
    const sweep = setInterval(refetch, 45000);

    return () => {
      cancelled = true;
      clearInterval(sweep);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomPcIdsKey, roomId]);

  // Room-wide sticky sync pause/resume: batch DB write (durable intent) +
  // fan-out pokes to online agents for immediate effect (triggerRoomUpdate shape).
  const pausedSyncPcs = useMemo(() => pcInstances.filter((pc) => pc.sync_paused), [pcInstances]);
  const handleRoomSyncPause = async (paused: boolean) => {
    if (pcInstances.length === 0 || togglingRoomSync) return;
    setTogglingRoomSync(true);
    try {
      let operator = '';
      try {
        const operatorData = sessionStorage.getItem('current_operator');
        if (operatorData) {
          const op = JSON.parse(operatorData);
          operator = op.name || op.operator_code || '';
        }
      } catch { /* display-only */ }

      const targets = paused ? pcInstances : pausedSyncPcs;
      await databaseService.setSyncPaused(targets.map((pc) => pc.id), paused, operator || undefined);
      const online = targets.filter((pc) => pc.status === 'online' && (pc.ip_address || pc.tailscale_ip));
      await Promise.allSettled(
        online.map(async (pc) => {
          const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
          return paused ? client.pauseSync(operator || undefined) : client.resumeSync(operator || undefined);
        })
      );
      handleRefresh();
    } finally {
      setTogglingRoomSync(false);
    }
  };

  // Room-level upstream-issue detection (client-side approximation, from the
  // diagnostics DB mirror on pc_instances): ≥2 ONLINE PCs failing the SAME
  // test id, where that test looks upstream (db_reachability, or the PC's
  // failure_code starts with convai_platform). One dismissible banner, no
  // extra per-tile alarm styling.
  const upstreamIssue = (() => {
    const counts = new Map<string, number>();
    const upstreamIds = new Set<string>();
    for (const pc of pcInstances) {
      if (pc.status !== 'online' || !pc.diag_detail) continue;
      const failing = new Set([
        ...(pc.diag_detail.failed || []),
        ...(pc.diag_detail.advisory || []),
      ]);
      for (const id of failing) {
        counts.set(id, (counts.get(id) || 0) + 1);
        if (id === 'db_reachability' || (pc.diag_detail.failure_code || '').startsWith('convai_platform')) {
          upstreamIds.add(id);
        }
      }
    }
    const shared = Array.from(counts.entries())
      .filter(([id, count]) => count >= 2 && upstreamIds.has(id))
      .map(([id]) => id)
      .sort();
    if (shared.length === 0) return null;
    return { ids: shared, key: shared.join(',') };
  })();

  // Queue mode replaces the tile grid with bars + the queue column. It needs a
  // location: without one there is no queue to put in the right column, so the
  // room falls back to the tile grid exactly as before. With the setting off,
  // nothing below this line changes at all.
  const queueColumnMode = queueEnabled && !!selectedRoom?.location;

  // One PC, rendered as a tile or as a bar. Same component, same props, same
  // behaviour — only `variant` differs (see components/PCTile).
  const renderPc = (pc: PCInstance, variant: 'tile' | 'bar') =>
    isFakePc(pc) ? (
      <FakePCTile
        key={pc.id}
        pcInstance={pc}
        experiences={experiences}
        isAdmin={isAdmin}
        onExperienceStateChange={handleExperienceStateChange}
        onToast={setToast}
        variant={variant}
      />
    ) : (
      <PCTile
        key={pc.id}
        pcInstance={pc}
        experiences={pcExperiencesMap.get(pc.id) || NO_EXPERIENCES}
        onRefresh={handleRefresh}
        isAdmin={isAdmin}
        onMultiplayerSelect={handleMultiplayerSelect}
        roomId={roomId}
        onExperienceStateChange={handleExperienceStateChange}
        variant={variant}
      />
    );

  const dashboard = (
    <div className="dashboard">
      {/* Error Display */}
      {error && <div className="error-banner">{error}</div>}

      {/* Room-wide upstream diagnostics banner */}
      {upstreamIssue && dismissedUpstreamKey !== upstreamIssue.key && (
        <div className="upstream-banner">
          <span>
            ⚠ Room-wide issue — likely not the PCs ({upstreamIssue.ids.map((id) => id.replace(/_/g, ' ')).join(', ')})
          </span>
          <button
            className="upstream-banner-dismiss"
            onClick={() => setDismissedUpstreamKey(upstreamIssue.key)}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Room-wide sync banner: live syncing count + the loud forgotten-pause
          indicator, with the room-wide pause/resume action inline. */}
      {!isLoading && (syncingPcIds.size > 0 || pausedSyncPcs.length > 0) && (
        <div className={`sync-room-banner${pausedSyncPcs.length > 0 ? ' paused' : ''}`}>
          <span>
            {syncingPcIds.size > 0 && `⇅ ${syncingPcIds.size} PC${syncingPcIds.size === 1 ? '' : "'s"} synchroniseren content`}
            {syncingPcIds.size > 0 && pausedSyncPcs.length > 0 && ' · '}
            {pausedSyncPcs.length > 0 && `⏸ SYNC GEPAUZEERD op ${pausedSyncPcs.length} PC${pausedSyncPcs.length === 1 ? '' : "'s"}${pausedSyncPcs[0].sync_paused_by ? ` (${pausedSyncPcs[0].sync_paused_by})` : ''}`}
          </span>
          {pausedSyncPcs.length > 0 ? (
            <button className="sync-banner-btn resume" onClick={() => handleRoomSyncPause(false)} disabled={togglingRoomSync}>
              {togglingRoomSync ? 'Bezig…' : '▶ Hervat alle syncs'}
            </button>
          ) : (
            <button className="sync-banner-btn" onClick={() => handleRoomSyncPause(true)} disabled={togglingRoomSync}>
              {togglingRoomSync ? 'Bezig…' : '⏸ Pauzeer alle syncs'}
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoading && <div className="loading">Loading room data...</div>}

      {/* Top row: the per-experience instruction buttons on the left, plus the
          queue-screen toggle in queue mode, with the room action toolbar
          (Mark Issue + wake actions) right-aligned at the same height. */}
      {!isLoading && selectedRoom && (() => {
        // The instruction images are per-VISITOR ("here is how the headset
        // goes on") and the queue is per-DAY, so turning the queue on has no
        // business hiding them. It used to: they lived in the else-branch of
        // this ternary and vanished the moment queueEnabled flipped (Dirk
        // 2026-09-12). Both now share one bar, instructions first because they
        // are pressed many times a day and the queue screen once.
        const instructionExperiences = experiences.filter(
          (exp) => exp.experiences?.instruction_image_path
        );
        // Same affordance for both: something the operator opens, not something
        // that takes over their screen. The queue screen button is hidden when a
        // second screen is already driving it, where it is simply always on.
        const showQueueScreenButton =
          queueEnabled &&
          !!selectedRoom.location &&
          !!displayPlacement &&
          !displayPlacement.hasSecondary;
        const leftContent =
          instructionExperiences.length === 0 && !showQueueScreenButton ? null : (
            <div className="instruction-buttons-bar">
              {instructionExperiences.map((exp) => (
                <button
                  key={exp.experience_id}
                  className={`instruction-btn ${activeInstructionId === exp.experience_id ? 'active' : ''}`}
                  onClick={() => handleInstructionToggle(exp)}
                  title={`Toon de instructies voor ${exp.experiences?.name}`}
                >
                  <span className="instruction-btn-icon">&#128196;</span>
                  {exp.experiences?.name}
                </button>
              ))}
              {showQueueScreenButton && (
                <button
                  className={`instruction-btn ${displayOpen ? 'active' : ''}`}
                  onClick={toggleQueueDisplay}
                  title={
                    displayOpen
                      ? 'Sluit het wachtrijscherm (of tik op het scherm zelf)'
                      : 'Toon het wachtrijscherm op dit scherm; tik erop om het weer te sluiten'
                  }
                >
                  <span className="instruction-btn-icon">&#128187;</span>
                  {displayOpen ? 'Sluit wachtrijscherm' : 'Wachtrijscherm'}
                </button>
              )}
            </div>
          );
        // Only offer the pause while something is actually downloading: a button
        // that pauses nothing is noise on a quiet room (Dirk 2026-09-10). The
        // resume path lives in the sync banner, which shows while syncs are paused.
        const showPreemptiveSyncPause = pausedSyncPcs.length === 0 && syncingPcIds.size > 0;
        const showMapButton = pcInstances.length > 0;
        const showActions = showMapButton || canMarkIssue || pcInstances.some((pc) => pc.status !== 'online') || showPreemptiveSyncPause;
        if (!leftContent && !showActions) return null;
        return (
          <div className="dashboard-top-row">
            <div className="dashboard-top-left">{leftContent}</div>
            {showActions && (
              <div className="room-actions-bar">
                {showMapButton && (
                  <button
                    className="playspace-map-btn"
                    onClick={() => setShowMap(true)}
                    title="Live kaart: waar staat elke speler in de ruimte (headset-tracking)"
                  >
                    🗺 Kaart
                  </button>
                )}
                {canMarkIssue && (
                  <button
                    className="mark-issue-btn"
                    onClick={() => setShowRoomMarkIssue(true)}
                    title="Mark a room-wide issue (not tied to one PC)"
                  >
                    ⚑ Mark Issue
                  </button>
                )}
                {showPreemptiveSyncPause && (
                  <button
                    className="sync-room-pause-btn"
                    onClick={() => handleRoomSyncPause(true)}
                    disabled={togglingRoomSync}
                    title="Pauzeer de lopende content-syncs in deze ruimte (voortgang blijft bewaard), bijv. vlak voor een sessie"
                  >
                    {togglingRoomSync ? 'Bezig…' : '⏸ Pauzeer syncs'}
                  </button>
                )}
                {pcInstances.some((pc) => pc.status !== 'online') && (
                  <>
                    {gatewayPcIds.length > 0 && offlineGatewayPcs().length > 0 && (
                      <button
                        className="wake-gateway-btn"
                        onClick={handlePowerOnGateways}
                        disabled={poweringGateway}
                        title="Schakelt de stroom van de gateway-pc uit en weer aan zodat hij opstart"
                      >
                        {poweringGateway ? 'Bezig…' : '⚡ Zet gateway aan'}
                      </button>
                    )}
                    <button className="wake-all-btn" onClick={handleWakeAllInRoom} disabled={wakingAll}>
                      {wakingAll ? 'Bezig…' : '⏻ Zet alle computers aan'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Web fallback for the instruction images. Not gated on queueEnabled:
          the buttons exist in both modes, so the modal has to as well. */}
      {instructionModalUrl && !window.electron && (
        <div className="instruction-modal-overlay" onClick={() => { setInstructionModalUrl(null); setActiveInstructionId(null); }}>
          <img
            className="instruction-modal-image"
            src={instructionModalUrl}
            alt="Experience Instructions"
          />
        </div>
      )}

      {/* PC Tiles — one grid for the whole room, ordered by player_index
          (a room's networks are an infra/sync detail, not an operator one).
          In queue mode the same tiles render as horizontal bars in a left
          column, with the queue in a right column (design/Main.dc.html). */}
      {!isLoading && selectedRoom && (
        <div className="networks-container">
          {sortedPcs.length === 0 ? (
            <div className="no-data">
              <p>No networks or PCs found in this room.</p>
            </div>
          ) : queueColumnMode ? (
            <div className="qdash">
              {/* Dense only for genuinely crowded rooms. The bars now grow into the
                  column's spare height (.qdash-bars flex), so a 10-PC room on a
                  tall screen wants comfortable bars, not the thin strip the old
                  six-PC threshold gave it. */}
              <div className={`qdash-left${lineupPcs.length > 12 ? ' qdash-left--dense' : ''}`}>
                <ComputersHeader total={lineupPcs.length} />
                <div className="qdash-bars">
                  {lineupPcs.map((pc) => renderPc(pc, 'bar'))}
                </div>
                {parkedPcs.length > 0 ? (
                  <details className="qdash-parked">
                    <summary>
                      <span className="qdash-parked-title">BUITEN DIENST</span>
                      <span className="qdash-parked-count">{parkedPcs.length}</span>
                    </summary>
                    <div className="qdash-bars qdash-bars--parked">
                      {parkedPcs.map((pc) => renderPc(pc, 'bar'))}
                    </div>
                  </details>
                ) : null}
              </div>
              <div className="qdash-right">
                <QueuePanel />
              </div>
            </div>
          ) : (
            /* Tile mode has no separate section, so the least it can do is
               keep the parked machines out of the middle of the grid. */
            <div className="pc-grid" style={getGridStyle(sortedPcs.length)}>
              {[...lineupPcs, ...parkedPcs].map((pc) => renderPc(pc, 'tile'))}
            </div>
          )}
        </div>
      )}

      {/* Multiplayer Session Modal */}
      {mpModalExperience && (
        <MultiplayerSessionModal
          experience={mpModalExperience}
          pcs={pcInstances}
          room={selectedRoom!}
          preSelectedPcId={mpPreSelectedPcId!}
          isAdmin={isAdmin}
          operatorId={(() => {
            try {
              const op = sessionStorage.getItem('current_operator');
              return op ? JSON.parse(op).id : undefined;
            } catch {
              return undefined;
            }
          })()}
          dequeueEntryIds={mpDequeueEntryIds ?? undefined}
          onClose={() => { setMpModalExperience(null); setMpPreSelectedPcId(null); setMpDequeueEntryIds(null); }}
        />
      )}

      {/* Live player map on the room plan */}
      {showMap && selectedRoom && (
        <PlayspaceMap
          room={selectedRoom}
          pcInstances={pcInstances}
          canSendVisitorMessage={isAdmin || operatorPermissions.includes('send_visitor_message')}
          onClose={() => setShowMap(false)}
        />
      )}

      {/* Room-scoped Mark Issue Modal */}
      {showRoomMarkIssue && (
        <MarkIssueModal
          contextLine={selectedRoom?.name ? `${selectedRoom.name} · room-wide issue` : 'Room-wide issue'}
          onCancel={() => setShowRoomMarkIssue(false)}
          onSubmit={handleRoomMarkIssueSubmit}
        />
      )}

      {/* Toast feedback for room-scoped marks */}
      {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}

      {/* No Room Selected */}
      {!selectedRoom && !isLoading && (
        <div className="no-selection">
          <p>Please select a room to view PCs and controls.</p>
        </div>
      )}
    </div>
  );

  // The queue's dequeue-on-launch wiring only needs to be in the tree when the
  // queue is enabled. When disabled, PCTile's useQueueDequeueOptional() simply
  // returns null and its dequeue logic stays dormant. The dispatch provider
  // (queue-first pop-up + undo-with-stop) nests inside it: tiles feed it PC
  // reports and launch functions via useQueueDispatchOptional().
  return queueEnabled ? (
    <QueueDequeueProvider locationId={selectedRoom?.location ?? null}>
      <QueueDispatchProvider
        locationId={selectedRoom?.location ?? null}
        pcs={pcInstances}
        pcExperiencesMap={dispatchPcExperiencesMap}
      >
        {dashboard}
        <DispatchModal
          pcExperiencesMap={dispatchPcExperiencesMap}
          onToast={setToast}
          onStartMultiplayer={handleMultiplayerSelect}
        />
      </QueueDispatchProvider>
    </QueueDequeueProvider>
  ) : dashboard;
}
